// src/utils/updateCustomerMetrics.js
// Utility functions to compute and update customer metrics

import { collection, getDocs, doc, updateDoc, query, where, getDoc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Helper function to get date only (without time) from a timestamp
 * Uses local timezone to ensure dates are grouped correctly
 * @param {any} timestamp - Firestore timestamp or Date object
 * @returns {string} - Date string in YYYY-MM-DD format (local timezone)
 */
const getDateOnly = (timestamp) => {
  let date;
  if (timestamp?.toDate) {
    date = timestamp.toDate();
  } else if (timestamp?.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    date = new Date(timestamp);
  }

  // Use local date components to avoid UTC timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`; // Returns YYYY-MM-DD in local timezone
};

/**
 * Calculates total visits based on unique transaction dates
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<number>} - Number of unique visit dates
 */
export const calculateTotalVisits = async (customerEmail, salonId) => {
  try {
    // Query all transactions for this customer
    const transactionsRef = collection(db, 'transactions');
    const q = query(
      transactionsRef,
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return 0;
    }

    // Extract unique dates (ignore time)
    const uniqueDates = new Set();
    querySnapshot.forEach(doc => {
      const data = doc.data();
      if (data.createdAt) {
        const dateOnly = getDateOnly(data.createdAt);
        uniqueDates.add(dateOnly);
      }
    });

    return uniqueDates.size;

  } catch (error) {
    console.error('Error calculating total visits:', error);
    throw error;
  }
};

/**
 * Updates a customer's totalVisits metric based on unique transaction dates
 * @param {string} customerId - The customer ID
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with success status and totalVisits count
 */
export const updateCustomerTotalVisits = async (customerId, customerEmail, salonId) => {
  try {
    console.log('Updating totalVisits for customer:', customerId);

    const totalVisits = await calculateTotalVisits(customerEmail, salonId);

    // Update customer's metrics.totalVisits
    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      'metrics.totalVisits': totalVisits,
      updatedAt: new Date()
    });

    console.log('Customer totalVisits updated to:', totalVisits);

    return {
      success: true,
      totalVisits,
      message: 'totalVisits updated successfully'
    };

  } catch (error) {
    console.error('Error updating customer totalVisits:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Updates a customer's lastVisit based on their most recent transaction
 * @param {string} customerId - The customer ID
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with success status and lastVisit date
 */
export const updateCustomerLastVisit = async (customerId, customerEmail, salonId) => {
  try {
    console.log('Updating lastVisit for customer:', customerId);

    // Query transactions for this customer (without orderBy to avoid index requirement)
    const transactionsRef = collection(db, 'transactions');
    const q = query(
      transactionsRef,
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log('No transactions found for customer:', customerId);
      return {
        success: true,
        lastVisit: null,
        message: 'No transactions found'
      };
    }

    // Sort transactions in JavaScript to find most recent
    const allTransactions = [];
    querySnapshot.forEach(doc => {
      allTransactions.push(doc.data());
    });

    allTransactions.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime; // Descending (most recent first)
    });

    // Get the most recent transaction
    const mostRecentTransaction = allTransactions[0];
    const lastVisitDate = mostRecentTransaction.createdAt;

    // Update customer's lastVisit
    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      lastVisit: lastVisitDate,
      updatedAt: new Date()
    });

    console.log('Customer lastVisit updated to:', lastVisitDate);

    return {
      success: true,
      lastVisit: lastVisitDate,
      message: 'lastVisit updated successfully'
    };

  } catch (error) {
    console.error('Error updating customer lastVisit:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Recalculates lastVisit for all customers in a salon
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with counts of updated customers
 */
export const recalculateAllCustomersLastVisit = async (salonId) => {
  try {
    console.log('Recalculating lastVisit for all customers in salon:', salonId);

    // Get all customers for this salon
    const customersRef = collection(db, 'customers');
    const customersQuery = query(
      customersRef,
      where('salonId', '==', salonId)
    );

    const customersSnapshot = await getDocs(customersQuery);

    if (customersSnapshot.empty) {
      console.log('No customers found for salon:', salonId);
      return {
        success: true,
        totalCustomers: 0,
        updated: 0,
        message: 'No customers found'
      };
    }

    let updated = 0;
    let failed = 0;

    // Process each customer
    for (const customerDoc of customersSnapshot.docs) {
      const customerData = customerDoc.data();
      const result = await updateCustomerLastVisit(
        customerDoc.id,
        customerData.email,
        salonId
      );

      if (result.success && result.lastVisit) {
        updated++;
      } else if (!result.success) {
        failed++;
      }
    }

    console.log(`Recalculation complete. Updated: ${updated}, Failed: ${failed}`);

    return {
      success: true,
      totalCustomers: customersSnapshot.size,
      updated,
      failed,
      message: `Updated ${updated} customers, ${failed} failed`
    };

  } catch (error) {
    console.error('Error recalculating lastVisit for all customers:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Recalculates totalVisits for all customers in a salon
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with counts of updated customers
 */
export const recalculateAllCustomersTotalVisits = async (salonId) => {
  try {
    console.log('Recalculating totalVisits for all customers in salon:', salonId);

    // Get all customers for this salon
    const customersRef = collection(db, 'customers');
    const customersQuery = query(
      customersRef,
      where('salonId', '==', salonId)
    );

    const customersSnapshot = await getDocs(customersQuery);

    if (customersSnapshot.empty) {
      console.log('No customers found for salon:', salonId);
      return {
        success: true,
        totalCustomers: 0,
        updated: 0,
        message: 'No customers found'
      };
    }

    let updated = 0;
    let failed = 0;

    // Process each customer
    for (const customerDoc of customersSnapshot.docs) {
      const customerData = customerDoc.data();
      const result = await updateCustomerTotalVisits(
        customerDoc.id,
        customerData.email,
        salonId
      );

      if (result.success) {
        updated++;
      } else {
        failed++;
      }
    }

    console.log(`Recalculation complete. Updated: ${updated}, Failed: ${failed}`);

    return {
      success: true,
      totalCustomers: customersSnapshot.size,
      updated,
      failed,
      message: `Updated ${updated} customers, ${failed} failed`
    };

  } catch (error) {
    console.error('Error recalculating totalVisits for all customers:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Utility function to be called from browser console or admin panel
 * Recalculates both lastVisit and totalVisits for all customers across all salons
 */
export const recalculateAllCustomerMetrics = async () => {
  try {
    console.log('Starting global customer metrics recalculation...');
    console.log('This will update both lastVisit and totalVisits for all customers');

    // Get all customers
    const customersRef = collection(db, 'customers');
    const customersSnapshot = await getDocs(customersRef);

    console.log(`Found ${customersSnapshot.size} customers to process`);

    let updated = 0;
    let skipped = 0;
    let failed = 0;

    for (const customerDoc of customersSnapshot.docs) {
      const customerData = customerDoc.data();
      const customerId = customerDoc.id;

      console.log(`Processing customer ${updated + skipped + failed + 1}/${customersSnapshot.size}: ${customerId}`);

      try {
        // Query all transactions for this customer (without orderBy to avoid index)
        const transactionsRef = collection(db, 'transactions');
        const q = query(
          transactionsRef,
          where('customerEmail', '==', customerData.email),
          where('salonId', '==', customerData.salonId)
        );

        const transactionsSnapshot = await getDocs(q);

        if (transactionsSnapshot.empty) {
          console.log(`  No transactions found for ${customerId}`);
          skipped++;
          continue;
        }

        // Sort and process transactions in JavaScript
        const allTransactions = [];
        transactionsSnapshot.forEach(doc => {
          allTransactions.push(doc.data());
        });

        allTransactions.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        // Get the most recent transaction for lastVisit
        const mostRecentTransaction = allTransactions[0];
        const lastVisitDate = mostRecentTransaction.createdAt;

        // Calculate totalVisits based on unique dates
        const uniqueDates = new Set();
        allTransactions.forEach(data => {
          if (data.createdAt) {
            const dateOnly = getDateOnly(data.createdAt);
            uniqueDates.add(dateOnly);
          }
        });
        const totalVisits = uniqueDates.size;

        // Update customer document
        const customerRef = doc(db, 'customers', customerId);
        await updateDoc(customerRef, {
          lastVisit: lastVisitDate,
          'metrics.totalVisits': totalVisits,
          updatedAt: new Date()
        });

        console.log(`  ✓ Updated lastVisit: ${lastVisitDate.toDate ? lastVisitDate.toDate() : lastVisitDate}`);
        console.log(`  ✓ Updated totalVisits: ${totalVisits}`);
        updated++;

      } catch (error) {
        console.error(`  ✗ Failed to update ${customerId}:`, error.message);
        failed++;
      }
    }

    console.log('='.repeat(60));
    console.log('Global Customer Metrics Recalculation Complete');
    console.log('='.repeat(60));
    console.log(`Total customers processed: ${customersSnapshot.size}`);
    console.log(`Successfully updated: ${updated}`);
    console.log(`Skipped (no transactions): ${skipped}`);
    console.log(`Failed: ${failed}`);
    console.log('='.repeat(60));

    return {
      success: true,
      totalCustomers: customersSnapshot.size,
      updated,
      skipped,
      failed
    };

  } catch (error) {
    console.error('Error in global customer metrics recalculation:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Utility function to be called from browser console or admin panel
 * Recalculates lastVisit for all customers across all salons
 */
export const recalculateAllLastVisits = async () => {
  try {
    console.log('Starting global lastVisit recalculation...');

    // Get all customers
    const customersRef = collection(db, 'customers');
    const customersSnapshot = await getDocs(customersRef);

    console.log(`Found ${customersSnapshot.size} customers to process`);

    let updated = 0;
    let skipped = 0;
    let failed = 0;

    for (const customerDoc of customersSnapshot.docs) {
      const customerData = customerDoc.data();
      const customerId = customerDoc.id;

      console.log(`Processing customer ${updated + skipped + failed + 1}/${customersSnapshot.size}: ${customerId}`);

      // Query transactions for this customer (without orderBy to avoid index)
      const transactionsRef = collection(db, 'transactions');
      const q = query(
        transactionsRef,
        where('customerEmail', '==', customerData.email),
        where('salonId', '==', customerData.salonId)
      );

      try {
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          console.log(`  No transactions found for ${customerId}`);
          skipped++;
          continue;
        }

        // Sort in JavaScript to find most recent
        const allTransactions = [];
        querySnapshot.forEach(doc => {
          allTransactions.push(doc.data());
        });

        allTransactions.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        // Get the most recent transaction
        const mostRecentTransaction = allTransactions[0];
        const lastVisitDate = mostRecentTransaction.createdAt;

        // Update customer's lastVisit
        const customerRef = doc(db, 'customers', customerId);
        await updateDoc(customerRef, {
          lastVisit: lastVisitDate,
          updatedAt: new Date()
        });

        console.log(`  ✓ Updated lastVisit to ${lastVisitDate.toDate ? lastVisitDate.toDate() : lastVisitDate}`);
        updated++;

      } catch (error) {
        console.error(`  ✗ Failed to update ${customerId}:`, error.message);
        failed++;
      }
    }

    console.log('='.repeat(60));
    console.log('Global lastVisit Recalculation Complete');
    console.log('='.repeat(60));
    console.log(`Total customers processed: ${customersSnapshot.size}`);
    console.log(`Successfully updated: ${updated}`);
    console.log(`Skipped (no transactions): ${skipped}`);
    console.log(`Failed: ${failed}`);
    console.log('='.repeat(60));

    return {
      success: true,
      totalCustomers: customersSnapshot.size,
      updated,
      skipped,
      failed
    };

  } catch (error) {
    console.error('Error in global lastVisit recalculation:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Calculates total spent (sum of totalAmount) from ALL transactions for a customer
 * No time limit - includes all historical transactions
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<number>} - Total amount spent across all transactions
 */
export const calculateTotalSpent = async (customerEmail, salonId) => {
  try {
    // Query all transactions for this customer
    const transactionsRef = collection(db, 'transactions');
    const q = query(
      transactionsRef,
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return 0;
    }

    // Sum amounts from Completed transactions only — #Revised UI_v2
    let totalSpent = 0;
    querySnapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.paymentStatus !== 'Completed') return; // Only count completed payments
      // Use price field first (how transactions are saved), then totalAmount, then amount
      const amount = parseFloat(data.price) || parseFloat(data.totalAmount) || parseFloat(data.amount) || 0;
      totalSpent += amount;
    });

    return totalSpent;

  } catch (error) {
    console.error('Error calculating total spent:', error);
    return 0;
  }
};

/**
 * Updates a customer's totalSpent metric based on sum of all transaction totalAmounts
 * @param {string} customerId - The customer document ID
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with success status and totalSpent
 */
export const updateCustomerTotalSpent = async (customerId, customerEmail, salonId) => {
  try {
    console.log('Calculating totalSpent for customer:', customerId);

    // Calculate total spent from all transactions
    const totalSpent = await calculateTotalSpent(customerEmail, salonId);
    console.log('Total spent calculated:', totalSpent);

    // Update customer document
    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      'metrics.totalSpent': totalSpent,
      updatedAt: new Date()
    });

    console.log('Customer totalSpent updated to:', totalSpent);

    return {
      success: true,
      totalSpent,
      message: 'totalSpent updated successfully'
    };

  } catch (error) {
    console.error('Error updating customer totalSpent:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Recalculates totalSpent for all customers in a salon
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with counts of updated customers
 */
export const recalculateAllCustomersTotalSpent = async (salonId) => {
  try {
    console.log('Recalculating totalSpent for all customers in salon:', salonId);

    // Get all customers for this salon
    const customersRef = collection(db, 'customers');
    const customersQuery = query(
      customersRef,
      where('salonId', '==', salonId)
    );

    const customersSnapshot = await getDocs(customersQuery);

    if (customersSnapshot.empty) {
      console.log('No customers found for salon:', salonId);
      return {
        success: true,
        totalCustomers: 0,
        updated: 0,
        message: 'No customers found'
      };
    }

    let updated = 0;
    let failed = 0;

    // Process each customer
    for (const customerDoc of customersSnapshot.docs) {
      const customerData = customerDoc.data();

      try {
        const totalSpent = await calculateTotalSpent(customerData.email, salonId);

        // Update customer document
        const customerRef = doc(db, 'customers', customerDoc.id);
        await updateDoc(customerRef, {
          'metrics.totalSpent': totalSpent,
          updatedAt: new Date()
        });

        console.log(`  ✓ ${customerDoc.id}: $${totalSpent.toFixed(2)}`);
        updated++;

      } catch (error) {
        console.error(`  ✗ Failed to update ${customerDoc.id}:`, error.message);
        failed++;
      }
    }

    console.log(`Recalculation complete. Updated: ${updated}, Failed: ${failed}`);

    return {
      success: true,
      totalCustomers: customersSnapshot.size,
      updated,
      failed,
      message: `Updated ${updated} customers, ${failed} failed`
    };

  } catch (error) {
    console.error('Error recalculating totalSpent for all customers:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Calculates total spent (sum of totalAmount) from transactions in the last 365 days
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<number>} - Total amount spent in the last 365 days
 */
export const calculateTotalSpentLast365Days = async (customerEmail, salonId) => {
  try {
    // Calculate date 365 days ago
    const now = new Date();
    const oneYearAgo = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));

    // Query transactions for this customer
    const transactionsRef = collection(db, 'transactions');
    const q = query(
      transactionsRef,
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return 0;
    }

    // Sum totalAmount for transactions in the last 365 days
    let totalSpent = 0;
    querySnapshot.forEach(docSnap => {
      const data = docSnap.data();

      // Check if transaction is within the last 365 days
      let transactionDate;
      if (data.createdAt?.toDate) {
        transactionDate = data.createdAt.toDate();
      } else if (data.createdAt?.seconds) {
        transactionDate = new Date(data.createdAt.seconds * 1000);
      } else if (data.createdAt instanceof Date) {
        transactionDate = data.createdAt;
      } else if (data.createdAt) {
        transactionDate = new Date(data.createdAt);
      } else {
        return; // Skip if no createdAt date
      }

      if (transactionDate >= oneYearAgo && data.paymentStatus === 'Completed') { // #Revised UI_v2 — only completed
        const amount = parseFloat(data.price) || parseFloat(data.totalAmount) || parseFloat(data.amount) || 0;
        totalSpent += amount;
      }
    });

    return totalSpent;

  } catch (error) {
    console.error('Error calculating total spent for last 365 days:', error);
    return 0;
  }
};

/**
 * Updates a customer's totalSpentLast365Days metric
 * @param {string} customerId - The customer document ID
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with success status and totalSpentLast365Days
 */
export const updateCustomerTotalSpentLast365Days = async (customerId, customerEmail, salonId) => {
  try {
    console.log('Calculating totalSpentLast365Days for customer:', customerId);

    // Calculate total spent in last 365 days
    const totalSpentLast365Days = await calculateTotalSpentLast365Days(customerEmail, salonId);
    console.log('Total spent in last 365 days:', totalSpentLast365Days);

    // Update customer document
    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      'metrics.totalSpentLast365Days': totalSpentLast365Days,
      updatedAt: new Date()
    });

    console.log('Customer totalSpentLast365Days updated to:', totalSpentLast365Days);

    return {
      success: true,
      totalSpentLast365Days,
      message: 'totalSpentLast365Days updated successfully'
    };

  } catch (error) {
    console.error('Error updating customer totalSpentLast365Days:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Recalculates totalSpentLast365Days for all customers in a salon
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with counts of updated customers
 */
export const recalculateAllCustomersTotalSpentLast365Days = async (salonId) => {
  try {
    console.log('Recalculating totalSpentLast365Days for all customers in salon:', salonId);

    // Get all customers for this salon
    const customersRef = collection(db, 'customers');
    const customersQuery = query(
      customersRef,
      where('salonId', '==', salonId)
    );

    const customersSnapshot = await getDocs(customersQuery);

    if (customersSnapshot.empty) {
      console.log('No customers found for salon:', salonId);
      return {
        success: true,
        totalCustomers: 0,
        updated: 0,
        message: 'No customers found'
      };
    }

    let updated = 0;
    let failed = 0;

    // Process each customer
    for (const customerDoc of customersSnapshot.docs) {
      const customerData = customerDoc.data();

      try {
        const totalSpentLast365Days = await calculateTotalSpentLast365Days(customerData.email, salonId);

        // Update customer document
        const customerRef = doc(db, 'customers', customerDoc.id);
        await updateDoc(customerRef, {
          'metrics.totalSpentLast365Days': totalSpentLast365Days,
          updatedAt: new Date()
        });

        console.log(`  ✓ ${customerDoc.id}: $${totalSpentLast365Days.toFixed(2)} (last 365 days)`);
        updated++;

      } catch (error) {
        console.error(`  ✗ Failed to update ${customerDoc.id}:`, error.message);
        failed++;
      }
    }

    console.log(`Recalculation complete. Updated: ${updated}, Failed: ${failed}`);

    return {
      success: true,
      totalCustomers: customersSnapshot.size,
      updated,
      failed,
      message: `Updated ${updated} customers, ${failed} failed`
    };

  } catch (error) {
    console.error('Error recalculating totalSpentLast365Days for all customers:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Calculates total points earned in the last 365 days for a customer
 * Only considers transactions with paymentStatus = "Completed"
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<number>} - Total points earned in the last 365 days
 */
export const calculatePointsLast365Days = async (customerEmail, salonId) => {
  try {
    // Calculate date 365 days ago
    const now = new Date();
    const oneYearAgo = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));

    // Query transactions for this customer
    const transactionsRef = collection(db, 'transactions');
    const q = query(
      transactionsRef,
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return 0;
    }

    // Sum pointsEarned for transactions in the last 365 days with paymentStatus = "Completed"
    let totalPoints = 0;
    querySnapshot.forEach(docSnap => {
      const data = docSnap.data();

      // Only consider completed transactions (handle both 'Completed' and 'completed')
      const paymentStatus = (data.paymentStatus || '').toLowerCase();
      if (paymentStatus !== 'completed') {
        return;
      }

      // Check if transaction is within the last 365 days
      let transactionDate;
      if (data.createdAt?.toDate) {
        transactionDate = data.createdAt.toDate();
      } else if (data.createdAt?.seconds) {
        transactionDate = new Date(data.createdAt.seconds * 1000);
      } else if (data.createdAt instanceof Date) {
        transactionDate = data.createdAt;
      } else if (data.createdAt) {
        transactionDate = new Date(data.createdAt);
      } else {
        return; // Skip if no createdAt date
      }

      if (transactionDate >= oneYearAgo) {
        totalPoints += (data.pointsEarned || 0);
      }
    });

    return totalPoints;

  } catch (error) {
    console.error('Error calculating points for last 365 days:', error);
    return 0;
  }
};

/**
 * Determines the loyalty tier based on points and tier settings
 * @param {number} points - Total points earned in the last 365 days
 * @param {Array} tiers - Array of tier objects with {name, minPoints, maxPoints}
 * @returns {string} - The tier name (e.g., 'Bronze', 'Silver', 'Gold', 'Platinum')
 */
export const determineLoyaltyTier = (points, tiers) => {
  if (!tiers || tiers.length === 0) {
    return 'Bronze'; // Default tier if no tiers configured
  }

  // Sort tiers by minPoints ascending to ensure proper order
  const sortedTiers = [...tiers].sort((a, b) => a.minPoints - b.minPoints);

  for (const tier of sortedTiers) {
    const maxPoints = tier.maxPoints === null ? Infinity : tier.maxPoints;
    if (points >= tier.minPoints && points <= maxPoints) {
      return tier.name;
    }
  }

  // If points exceed all tiers, return the highest tier
  return sortedTiers[sortedTiers.length - 1]?.name || 'Bronze';
};

/**
 * Fetches loyalty tier settings from a salon document
 * @param {string} salonId - The salon ID
 * @returns {Promise<Array>} - Array of tier objects
 */
export const getLoyaltyTierSettings = async (salonId) => {
  try {
    const salonRef = doc(db, 'salons', salonId);
    const salonDoc = await getDoc(salonRef);

    if (!salonDoc.exists()) {
      console.warn('Salon document not found:', salonId);
      return getDefaultTiers();
    }

    const salonData = salonDoc.data();
    const tiers = salonData?.loyaltyProgram?.tiers;

    if (!tiers || tiers.length === 0) {
      return getDefaultTiers();
    }

    return tiers;

  } catch (error) {
    console.error('Error fetching loyalty tier settings:', error);
    return getDefaultTiers();
  }
};

/**
 * Returns default loyalty tiers
 * @returns {Array} - Default tier configuration
 */
export const getDefaultTiers = () => {
  return [
    { name: 'Bronze', minPoints: 0, maxPoints: 499 },
    { name: 'Silver', minPoints: 500, maxPoints: 999 },
    { name: 'Gold', minPoints: 1000, maxPoints: 1999 },
    { name: 'Platinum', minPoints: 2000, maxPoints: null }
  ];
};

/**
 * Calculates and updates the loyalty tier for a customer
 * Based on sum of pointsEarned from completed transactions in the last 365 days
 * @param {string} customerId - The customer document ID
 * @param {string} customerEmail - The customer's email
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with success status, points, and tier
 */
export const updateCustomerLoyaltyTier = async (customerId, customerEmail, salonId) => {
  try {
    console.log('Calculating loyalty tier for customer:', customerId);

    // Get points earned in the last 365 days
    const pointsLast365Days = await calculatePointsLast365Days(customerEmail, salonId);
    console.log('Points earned in last 365 days:', pointsLast365Days);

    // Get tier settings from salon
    const tiers = await getLoyaltyTierSettings(salonId);
    console.log('Tier settings:', tiers);

    // Determine the tier based on points
    const loyaltyTier = determineLoyaltyTier(pointsLast365Days, tiers);
    console.log('Determined loyalty tier:', loyaltyTier);

    // Update customer document with new tier
    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      loyaltyTier: loyaltyTier,
      'metrics.pointsLast365Days': pointsLast365Days,
      updatedAt: new Date()
    });

    console.log('Customer loyalty tier updated to:', loyaltyTier);

    return {
      success: true,
      pointsLast365Days,
      loyaltyTier,
      message: 'Loyalty tier updated successfully'
    };

  } catch (error) {
    console.error('Error updating customer loyalty tier:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Recalculates loyalty tier for all customers in a salon
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object>} - Result with counts of updated customers
 */
export const recalculateAllCustomersLoyaltyTiers = async (salonId) => {
  try {
    console.log('Recalculating loyalty tiers for all customers in salon:', salonId);

    // Get tier settings once for the salon
    const tiers = await getLoyaltyTierSettings(salonId);
    console.log('Using tier settings:', tiers);

    // Get all customers for this salon
    const customersRef = collection(db, 'customers');
    const customersQuery = query(
      customersRef,
      where('salonId', '==', salonId)
    );

    const customersSnapshot = await getDocs(customersQuery);

    if (customersSnapshot.empty) {
      console.log('No customers found for salon:', salonId);
      return {
        success: true,
        totalCustomers: 0,
        updated: 0,
        message: 'No customers found'
      };
    }

    let updated = 0;
    let failed = 0;

    // Process each customer
    for (const customerDoc of customersSnapshot.docs) {
      const customerData = customerDoc.data();

      try {
        // Calculate points for this customer
        const pointsLast365Days = await calculatePointsLast365Days(
          customerData.email,
          salonId
        );

        // Determine tier
        const loyaltyTier = determineLoyaltyTier(pointsLast365Days, tiers);

        // Update customer document
        const customerRef = doc(db, 'customers', customerDoc.id);
        await updateDoc(customerRef, {
          loyaltyTier: loyaltyTier,
          'metrics.pointsLast365Days': pointsLast365Days,
          updatedAt: new Date()
        });

        console.log(`  ✓ ${customerDoc.id}: ${pointsLast365Days} points -> ${loyaltyTier}`);
        updated++;

      } catch (error) {
        console.error(`  ✗ Failed to update ${customerDoc.id}:`, error.message);
        failed++;
      }
    }

    console.log(`Recalculation complete. Updated: ${updated}, Failed: ${failed}`);

    return {
      success: true,
      totalCustomers: customersSnapshot.size,
      updated,
      failed,
      message: `Updated ${updated} customers, ${failed} failed`
    };

  } catch (error) {
    console.error('Error recalculating loyalty tiers for all customers:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Make functions available in browser console for manual execution
if (typeof window !== 'undefined') {
  window.calculateTotalVisits = calculateTotalVisits;
  window.updateCustomerTotalVisits = updateCustomerTotalVisits;
  window.updateCustomerLastVisit = updateCustomerLastVisit;
  window.recalculateAllCustomersLastVisit = recalculateAllCustomersLastVisit;
  window.recalculateAllCustomersTotalVisits = recalculateAllCustomersTotalVisits;
  window.recalculateAllCustomerMetrics = recalculateAllCustomerMetrics;
  window.recalculateAllLastVisits = recalculateAllLastVisits;
  window.calculatePointsLast365Days = calculatePointsLast365Days;
  window.determineLoyaltyTier = determineLoyaltyTier;
  window.getLoyaltyTierSettings = getLoyaltyTierSettings;
  window.updateCustomerLoyaltyTier = updateCustomerLoyaltyTier;
  window.recalculateAllCustomersLoyaltyTiers = recalculateAllCustomersLoyaltyTiers;
  window.calculateTotalSpent = calculateTotalSpent;
  window.updateCustomerTotalSpent = updateCustomerTotalSpent;
  window.recalculateAllCustomersTotalSpent = recalculateAllCustomersTotalSpent;
  window.calculateTotalSpentLast365Days = calculateTotalSpentLast365Days;
  window.updateCustomerTotalSpentLast365Days = updateCustomerTotalSpentLast365Days;
  window.recalculateAllCustomersTotalSpentLast365Days = recalculateAllCustomersTotalSpentLast365Days;
}

